import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
    AbstractStepDTO,
    ChangeDataEventStepDTO,
    ConditionIfStepDTO,
    ConfiguratorDataViewService,
    ConfiguratorLandingPageService,
    ConfiguratorSaveQueryService,
    ConfiguratorTableService,
    ConfiguratorTransactionalEmailService,
    FieldTable,
    Table
} from '@tj/tj-dmp-lib-api';
import { parseType } from '../filter/filter.utils';
import { resolveI18nLabel } from '../../pipes/i18n-label.pipe';
import { LangService } from '../../services/lang.service';
import { FilterGetterListService } from '../../services/filter-getter-list.service';

const KEY = 'components.workflowStepConfig';

/** Libellés nécessaires pour décrire les étapes d'un scénario (noms de template, vues, tables…). */
export interface StepDescriptionLookups {
    tables: Table[];
    dataViews: { [code: string]: string };
    emails: { [id: string]: string };
    landingPages: { [id: string]: string };
    /** Libellés des résultats de formulaire (landingpage.result), par code */
    landingPageResults: { [code: string]: string };
    saveQueries: { [id: string]: string };
    /** Libellés des valeurs de liste des conditions, par type de valeur puis par code */
    listValues: { [valueType: string]: { [code: string]: string } };
}

export interface ConditionFieldInfo {
    label: string;
    tableLabel: string;
    variableType: string;
    valueType: string;
}

/**
 * Description lisible des étapes de scénario, générée dans la langue courante.
 * La description enregistrée avec l'étape est figée dans la langue de saisie : elle ne sert que de repli.
 */
@Injectable({ providedIn: 'root' })
export class StepDescriptionService {

    constructor(private translate: TranslateService,
                private langService: LangService,
                private tableService: ConfiguratorTableService,
                private dataViewService: ConfiguratorDataViewService,
                private emailService: ConfiguratorTransactionalEmailService,
                private landingPageService: ConfiguratorLandingPageService,
                private saveQueryService: ConfiguratorSaveQueryService,
                private filterGetterListService: FilterGetterListService) {
    }

    loadLookups(tenant: string, partner: string, steps: AbstractStepDTO[]): Observable<StepDescriptionLookups> {
        const viewCodes = Array.from(new Set(steps
            .filter(step => step.type === AbstractStepDTO.TypeEnum.SAVEQUERY)
            .map(step => (step as any).codeDataView)
            .filter(code => !!code)));
        const saveQueries$ = viewCodes.length === 0 ? of([]) : forkJoin(viewCodes.map(code =>
            this.saveQueryService.getAllSaveQuerysByDatastoreUsingGET(tenant, partner, code).pipe(catchError(() => of([])))));
        return forkJoin({
            tables: this.tableService.getTablesUsingGET(tenant).pipe(catchError(() => of([] as Table[]))),
            dataViews: this.dataViewService.getAllDataViewsUsingGET(tenant).pipe(catchError(() => of([]))),
            emails: this.emailService.getAllTransactionalEmailsUsingGET1(tenant, 0, 100).pipe(catchError(() => of([]))),
            landingPages: this.landingPageService.getAllLandingPagesUsingGET(tenant, 0, 100, undefined, undefined, true)
                .pipe(catchError(() => of([]))),
            saveQueries: saveQueries$
        }).pipe(map(res => {
            const lang = this.langService.currentLang;
            const byId = (items: { id?: string, name?: string }[]) =>
                (items ?? []).reduce((acc, item) => ({ ...acc, [item.id]: item.name }), {} as { [id: string]: string });
            return {
                tables: res.tables ?? [],
                dataViews: (res.dataViews ?? []).reduce((acc, view) =>
                    ({ ...acc, [view.code]: resolveI18nLabel(view.labels, lang, view.name) }), {} as { [code: string]: string }),
                emails: byId(res.emails),
                landingPages: byId(res.landingPages),
                landingPageResults: (res.landingPages ?? []).reduce((acc, landingPage) => {
                    (landingPage.results ?? []).forEach(result => acc[result.code] =
                        result.labels?.[(lang || 'fr').toUpperCase()] || result.labels?.['FR'] || result.code);
                    return acc;
                }, {} as { [code: string]: string }),
                saveQueries: byId([].concat(...(res.saveQueries as any[][]))),
                listValues: {}
            } as StepDescriptionLookups;
        }), switchMap(lookups => this.loadListValues(tenant, steps, lookups)));
    }

    private loadListValues(tenant: string, steps: AbstractStepDTO[], lookups: StepDescriptionLookups): Observable<StepDescriptionLookups> {
        const valueTypes = Array.from(new Set(steps
            .filter(step => step.type === AbstractStepDTO.TypeEnum.CONDITIONIF)
            .map(step => this.findField(lookups.tables, (step as ConditionIfStepDTO).field)?.field)
            .filter(field => field && (field.categoryField === 'TAG' || parseType(field.categoryField, field.type) === 'list'))
            .map(field => field.categoryField)));
        if (valueTypes.length === 0) {
            return of(lookups);
        }
        return forkJoin(valueTypes.map(valueType =>
            from(this.filterGetterListService.getFilterListType(tenant, valueType)).pipe(catchError(() => of([])))
        )).pipe(map(lists => {
            valueTypes.forEach((valueType, i) => lookups.listValues[valueType] =
                (lists[i] ?? []).reduce((acc, item) => ({ ...acc, [item.code]: item.value }), {} as { [code: string]: string }));
            return lookups;
        }));
    }

    private findField(tables: Table[], fullName: string): { table: Table, field: FieldTable } | null {
        const [tableCode, fieldCode] = (fullName ?? '').split('.');
        const table = tables.find(t => t.code === tableCode);
        const field = table?.fields?.find(f => f.code === fieldCode);
        return field ? { table, field } : null;
    }

    /** @return la description dans la langue courante, ou null si elle ne peut pas être reconstruite */
    describe(step: AbstractStepDTO, lookups: StepDescriptionLookups): string | null {
        const s = step as any;
        switch (step?.type) {
            case AbstractStepDTO.TypeEnum.DELAY:
                return this.delay(s.days, s.hours, s.minutes);
            case AbstractStepDTO.TypeEnum.EMAIL:
                return s.emailTemplateId ? this.email(lookups.emails[s.emailTemplateId] || s.emailTemplateId) : null;
            case AbstractStepDTO.TypeEnum.LANDINGPAGEEVENT:
                return s.idLandingPage ? this.landingPageEvent(lookups.landingPages[s.idLandingPage] || s.idLandingPage) : null;
            case AbstractStepDTO.TypeEnum.EMAILEVENT:
                return s.templateId
                    ? this.emailEvent(s.emailEvents ?? [], lookups.emails[s.templateId] || s.templateId)
                    : null;
            case AbstractStepDTO.TypeEnum.SAVEQUERY:
                return s.codeDataView
                    ? this.saveQuery(lookups.dataViews[s.codeDataView] || s.codeDataView, lookups.saveQueries[s.idSaveQuery] || s.idSaveQuery)
                    : null;
            case AbstractStepDTO.TypeEnum.CHANGEDATAEVENT:
                return this.describeChangeData(s as ChangeDataEventStepDTO, lookups.tables);
            case AbstractStepDTO.TypeEnum.CONDITIONIF:
                return this.describeCondition(s as ConditionIfStepDTO, lookups);
            default:
                return null;
        }
    }

    delay(days: number, hours: number, minutes: number): string {
        const parts: string[] = [];
        if (days > 0) {
            parts.push(`${days} ${this.t(`delayStepConfig.duration.${days === 1 ? 'day' : 'days'}`)}`);
        }
        if (hours > 0) {
            parts.push(`${hours} ${this.t(`delayStepConfig.duration.${hours === 1 ? 'hour' : 'hours'}`)}`);
        }
        if (minutes > 0) {
            parts.push(`${minutes} ${this.t(`delayStepConfig.duration.${minutes === 1 ? 'minute' : 'minutes'}`)}`);
        }
        const totalDuration = parts.length > 0 ? parts.join(', ') : this.t('delayStepConfig.duration.noDelay');
        return `${this.t('delayStepConfig.duration.delayPrefix')} ${totalDuration}`;
    }

    email(template: string): string {
        return this.t('emailStepConfig.descriptionTemplate', { template });
    }

    sms(template: string): string {
        return this.t('smsStepConfig.descriptionTemplate', { template });
    }

    formResultCondition(result: string): string {
        return this.t('conditionStepConfig.descriptionConfig.formResult', { result });
    }

    landingPageEvent(landingPage: string): string {
        return this.t('landingPageEventStepConfig.descriptionTemplate', { landingPage });
    }

    emailEventLabel(event: string): string {
        return this.t(`emailEventStepConfig.event.${event.toLowerCase()}`);
    }

    emailEvent(events: string[], template: string): string {
        return this.t('emailEventStepConfig.descriptionTemplate', {
            template,
            events: events.map(e => this.emailEventLabel(e)).join(', ')
        });
    }

    saveQuery(dataView: string, saveQuery: string): string {
        return this.t('saveQueryStepConfig.descriptionTemplate', { dataView, saveQuery });
    }

    changeTypeLabel(changeType: string): string {
        return this.t(`changeDataEventConfig.changeType.options.${changeType.toLowerCase()}`);
    }

    originLabel(origin: string): string {
        return this.t(`changeDataEventConfig.origins.options.${origin.toLowerCase()}`);
    }

    changeData(changeType: string, fieldLabels: string[], origins: string[]): string {
        let description = this.t('changeDataEventConfig.descriptionTemplate', {
            changeType: this.changeTypeLabel(changeType),
            fields: fieldLabels.join(', ')
        });
        if (origins?.length > 0) {
            description += this.t('changeDataEventConfig.origins.descriptionSuffix', {
                origins: origins.map(origin => this.originLabel(origin)).join(', ')
            });
        }
        return description;
    }

    operatorLabel(operatorValue: string): string {
        return this.t(`conditionStepConfig.condition.${operatorValue.toLowerCase()}`);
    }

    condition(field: ConditionFieldInfo | null, operator: string, value: any, excludeYear?: boolean): string {
        if (!field) {
            return this.t('conditionStepConfig.descriptionConfig.notConfigured');
        }
        const fieldLabel = `${field.label} (${field.tableLabel})`;

        if (field.valueType === 'TAG' || field.variableType === 'list') {
            return this.formatListCondition(fieldLabel, value);
        }
        if (field.variableType === 'boolean') {
            return this.formatBooleanCondition(fieldLabel, value);
        }
        if (!operator) {
            return `${fieldLabel} ${this.t('conditionStepConfig.descriptionConfig.operatorNotDefined')}`;
        }

        const operatorLabel = this.operatorLabel(operator).toLowerCase();
        const isEmpty = operator.trim().includes('EMPTY');
        switch (field.variableType) {
            case 'string':
                return this.formatStringCondition(fieldLabel, operatorLabel, value, isEmpty);
            case 'number':
                return this.formatNumberCondition(fieldLabel, operator, operatorLabel, value, isEmpty);
            case 'date':
                return this.formatDateCondition(fieldLabel, operator, operatorLabel, value, isEmpty, excludeYear);
            default:
                return `${fieldLabel} ${operatorLabel} ${this.formatValue(value)}`;
        }
    }

    private describeChangeData(step: ChangeDataEventStepDTO, tables: Table[]): string | null {
        if (!step.changeType || !step.searchFields?.length) {
            return null;
        }
        const fieldLabels = step.searchFields.map(searchField => {
            const table = tables.find(t => t.code === searchField.codeTable);
            const tableLabel = this.tableLabel(table, searchField.codeTable);
            if (step.changeType === ChangeDataEventStepDTO.ChangeTypeEnum.UPDATE) {
                const field = table?.fields?.find(f => f.code === searchField.codeField);
                return `${field?.label || searchField.codeField} (${tableLabel})`;
            }
            return tableLabel;
        });
        return this.changeData(step.changeType, fieldLabels, step.origins ?? []);
    }

    private describeCondition(step: ConditionIfStepDTO, lookups: StepDescriptionLookups): string | null {
        if (step.field === 'landingpage.result') {
            const code = step.values?.[0];
            return code ? this.formResultCondition(lookups.landingPageResults?.[code] || code) : null;
        }
        const found = this.findField(lookups.tables, step.field);
        if (!found) {
            return null;
        }
        const { table, field } = found;
        const info: ConditionFieldInfo = {
            label: field.label || field.code,
            tableLabel: this.tableLabel(table, table.code),
            variableType: parseType(field.categoryField, field.type),
            valueType: field.categoryField
        };
        const values = step.values ?? [];
        let value: any = values;
        if (info.valueType === 'TAG' || info.variableType === 'list') {
            const labels = lookups.listValues[info.valueType] ?? {};
            value = values.map(code => ({ code, value: labels[code] }));
        } else if (info.variableType === 'boolean') {
            value = values[0] === 'true';
        } else if (values.length === 1 && !this.isMultiValueOperator(step.operator)) {
            value = values[0];
        }
        return this.condition(info, step.operator, value, step.excludeYear);
    }

    private isMultiValueOperator(operator: string): boolean {
        return [ConditionIfStepDTO.OperatorEnum.DATERANGE, ConditionIfStepDTO.OperatorEnum.DATESLIDINGRANGE,
            ConditionIfStepDTO.OperatorEnum.DATEEXACTLY, ConditionIfStepDTO.OperatorEnum.NUMBERRANGE]
            .includes(operator as ConditionIfStepDTO.OperatorEnum);
    }

    private tableLabel(table: Table, code: string): string {
        return table?.label?.trim().length > 0 ? table.label : code;
    }

    private formatListCondition(fieldLabel: string, value: any): string {
        if (!value || (Array.isArray(value) && value.length === 0)) {
            return `${fieldLabel} ${this.t('conditionStepConfig.descriptionConfig.valueNotDefined')}`;
        }
        if (Array.isArray(value)) {
            const labels = value.map(v => v.value || v.code || v).join(', ');
            return value.length === 1
                ? `${fieldLabel} = "${labels}"`
                : this.t('conditionStepConfig.descriptionConfig.in', { field: fieldLabel, values: labels });
        }
        const label = value.value || value.code || value;
        return `${fieldLabel} = "${label}"`;
    }

    private formatBooleanCondition(fieldLabel: string, value: any): string {
        const booleanLabel = value
            ? this.t('conditionStepConfig.fields.value.booleanTrue')
            : this.t('conditionStepConfig.fields.value.booleanFalse');
        return `${fieldLabel} = ${booleanLabel}`;
    }

    private formatStringCondition(fieldLabel: string, operatorLabel: string, value: any, isEmpty: boolean): string {
        if (isEmpty) {
            return `${fieldLabel} ${operatorLabel}`;
        }
        if (Array.isArray(value)) {
            const valueStr = value.map(v => '\'' + v + '\'').join(', ');
            return `${fieldLabel} ${operatorLabel} ${valueStr}`;
        }
        return `${fieldLabel} ${operatorLabel} '${value}'`;
    }

    private formatNumberCondition(fieldLabel: string, operatorValue: string, operatorLabel: string, value: any, isEmpty: boolean): string {
        if (operatorValue === ConditionIfStepDTO.OperatorEnum.NUMBERRANGE && Array.isArray(value)) {
            return this.t('conditionStepConfig.descriptionConfig.between', {
                field: fieldLabel,
                value1: value[0],
                value2: value[1],
                suffix: ''
            });
        }
        if (isEmpty) {
            return `${fieldLabel} ${operatorLabel}`;
        }
        if (Array.isArray(value)) {
            return `${fieldLabel} ${operatorLabel} [${value.join(', ')}]`;
        }
        return `${fieldLabel} ${operatorLabel} ${value}`;
    }

    private formatDateCondition(fieldLabel: string, operatorValue: string, operatorLabel: string, value: any,
                                isEmpty: boolean, excludeYear?: boolean): string {
        const yearSuffix = excludeYear ? this.t('conditionStepConfig.descriptionConfig.withoutYear') : '';

        if (operatorValue === ConditionIfStepDTO.OperatorEnum.DATERANGE && Array.isArray(value)) {
            return this.t('conditionStepConfig.descriptionConfig.between', {
                field: fieldLabel,
                value1: this.formatDateValue(value[0]),
                value2: this.formatDateValue(value[1]),
                suffix: yearSuffix
            });
        }
        if (operatorValue === ConditionIfStepDTO.OperatorEnum.DATESLIDINGRANGE && Array.isArray(value)) {
            return this.t('conditionStepConfig.descriptionConfig.inLast', {
                field: fieldLabel,
                delta: value[1],
                unit: this.getDateUnitLabel(value[0], value[1]),
                suffix: yearSuffix
            });
        }
        if (operatorValue === ConditionIfStepDTO.OperatorEnum.DATEEXACTLY && Array.isArray(value)) {
            return this.t('conditionStepConfig.descriptionConfig.on', {
                field: fieldLabel,
                dates: value.map(v => this.formatDateValue(v)).join(', '),
                suffix: yearSuffix
            });
        }
        if (isEmpty) {
            return `${fieldLabel} ${operatorLabel}`;
        }
        if (value instanceof Date) {
            return `${fieldLabel} ${operatorLabel} ${this.formatDateValue(value)} ${yearSuffix}`;
        }
        return `${fieldLabel} ${operatorLabel} ${value} ${yearSuffix}`;
    }

    private formatDateValue(date: Date | string): string {
        if (!date) {
            return '';
        }
        const dateObj = date instanceof Date ? date : new Date(date);
        if (isNaN(dateObj.getTime())) {
            return String(date);
        }
        return dateObj.toLocaleDateString(this.dateLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    private dateLocale(): string {
        switch (this.langService.currentLang) {
            case 'en':
                return 'en-GB';
            case 'it':
                return 'it-IT';
            default:
                return 'fr-FR';
        }
    }

    private getDateUnitLabel(unit: string, delta: number): string {
        const units = ['day', 'week', 'month', 'year'];
        const key = (unit ?? '').toLowerCase();
        if (!units.includes(key)) {
            return unit;
        }
        return this.t(`conditionStepConfig.descriptionConfig.dateUnits.${key}${delta > 1 ? 's' : ''}`);
    }

    private formatValue(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        if (Array.isArray(value)) {
            return `[${value.join(', ')}]`;
        }
        if (value instanceof Date) {
            return this.formatDateValue(value);
        }
        return String(value);
    }

    private t(key: string, params?: object): string {
        return this.translate.instant(`${KEY}.${key}`, params);
    }
}
